import { useMemo, useState } from "react";
import { useMall } from "@/context/MallContext";
import MallMap from "@/components/mall/map/MallMap";
import UnitPopup from "@/components/mall/map/UnitPopup";
import { buildPlan } from "@/components/mall/map/floorplan";
import { usePlanPick } from "@/components/mall/map/DirectoryHero";
import { ShopList } from "@/components/directory/ShopList";

/**
 * The top of every shop page: the directory folded to one line, and the map with this shop lit
 * in its own color. You always know where you are and how to get to the next shop.
 */
export function DirectoryStrip({ store, theme }) {
    const { floor } = useMall();
    const [hoverId, setHoverId] = useState(null);
    const plan = useMemo(() => buildPlan(floor), [floor]);
    const { pick, unit, closeUnit } = usePlanPick(plan);

    return (
        <section className="border-b border-zinc-200 bg-zinc-50" data-testid="directory-strip">
            <div className="mx-auto grid max-w-7xl gap-4 px-4 py-4 sm:px-6 lg:grid-cols-3">
                <div className="lg:col-span-1">
                    <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-500">You are in</p>
                    <p className="mt-1 font-display text-lg font-extrabold text-zinc-900" data-testid="strip-store-name">{store.name}</p>
                    <p className="mb-3 text-xs text-zinc-500">Highlighted on the map in this shop's color.</p>
                    <ShopList floor={floor} plan={plan} activeSlug={store.slug} collapsible
                        onHover={(slug) => {
                            const s = plan.slots.find((x) => x.store?.slug === slug) || (plan.pads || []).find((p) => p.store?.slug === slug);
                            setHoverId(s ? s.id : null);
                        }} />
                </div>
                <div className="h-[260px] lg:col-span-2 lg:h-[300px]">
                    <MallMap plan={plan} hoverId={hoverId} onHover={(s) => setHoverId(s ? s.id : null)} onPick={pick} compact
                        highlight={{ slug: store.slug, color: theme.primary }} />
                </div>
            </div>
            {unit && <UnitPopup slot={unit} onClose={closeUnit} />}
        </section>
    );
}
